import { parseAllDocuments, stringify } from "yaml";
import type {
  AppType,
  AuthMethod,
  ClientManifest,
  CreatePlatformClientSpec,
} from "../api/types";

// The same file the CLI export writes (ADR-0050 decision 5): a leading comment
// naming the fields a promotion usually edits, then one client per document,
// `---` separated, keys in the order the API returns them.
// The CLI export drops keys whose value is null, so the Portal download does too.
function withoutNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutNulls);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== null)
        .map(([k, v]) => [k, withoutNulls(v)]),
    );
  }
  return value;
}

export function manifestsToYaml(
  manifests: ClientManifest[],
  envVaryingFields: string[],
): string {
  const header =
    envVaryingFields.length > 0
      ? `# Fields that usually differ between environments: ${envVaryingFields.join(", ")}\n`
      : "";
  return (
    header + manifests.map((m) => stringify(withoutNulls(m))).join("---\n")
  );
}

export class ManifestParseError extends Error {}

// One document, keys in the order the API returns them. The same file the CLI
// `blueprints export` writes (ADR-0051).
export function manifestToYaml(manifest: object): string {
  return stringify(withoutNulls(manifest));
}

// Exactly one YAML document that is a mapping. Throws ManifestParseError with a
// plain-English reason otherwise.
export function yamlToManifest<T>(text: string): T {
  const docs = parseAllDocuments(text).filter((d) => d.contents !== null);
  if (docs.length === 0) {
    throw new ManifestParseError("The manifest has no documents.");
  }
  if (docs.length > 1) {
    throw new ManifestParseError(
      "A blueprint manifest holds one platform. Remove the extra documents.",
    );
  }
  if (docs[0].errors.length > 0) {
    throw new ManifestParseError(
      `The manifest is not valid YAML: ${docs[0].errors[0].message}`,
    );
  }
  const value = docs[0].toJS();
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ManifestParseError("The manifest must have named fields.");
  }
  return value as T;
}

// One ClientManifest per YAML document. Throws ManifestParseError with a
// plain-English reason when the text is not a list of mappings.
export function yamlToManifests(text: string): ClientManifest[] {
  const manifests: ClientManifest[] = [];
  for (const doc of parseAllDocuments(text)) {
    if (doc.errors.length > 0) {
      throw new ManifestParseError(
        `Document ${manifests.length + 1} is not valid YAML: ${doc.errors[0].message}`,
      );
    }
    const value = doc.toJS();
    if (value === null || value === undefined) continue;
    if (typeof value !== "object" || Array.isArray(value)) {
      throw new ManifestParseError(
        `Document ${manifests.length + 1} must be a client with named fields.`,
      );
    }
    manifests.push(value as ClientManifest);
  }
  if (manifests.length === 0) {
    throw new ManifestParseError("The manifest has no documents.");
  }
  return manifests;
}

// The manifest editor grows with its text, so the whole manifest is visible
// without an inner scrollbar. A short manifest still gets a large field.
export function editorRows(text: string): number {
  return Math.max(24, text.split("\n").length + 1);
}

// Bifrost gives .bf-textarea a fixed 100px height and a 500px cap, which
// override `rows`. Clear both so editorRows sets the height.
export const editorStyle = { height: "auto", maxHeight: "none" } as const;

export function downloadTextFile(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/yaml" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// A manifest document as a client of the platform being created. The platform is
// never in the manifest: the wizard supplies it (ADR-0050 decision 2).
export function manifestToClientSpec(
  m: ClientManifest,
): CreatePlatformClientSpec {
  return {
    appType: m.type as AppType,
    purposeTag: m.purposeTag ?? "",
    // Carried like purposeTag (ADR-0060 decision 7); wiredUpAt never is.
    stack: m.stack,
    name: m.name,
    slug: m.clientId,
    description: m.description,
    tags: m.tags,
    redirectUris: m.redirectUris,
    audienceGrants: m.audiences,
    authMethod: m.authMethod as AuthMethod | undefined,
    spiffeNamespace: m.spiffe?.namespace,
    spiffeServiceAccount: m.spiffe?.serviceAccount,
    spiffeCluster: m.spiffe?.cluster,
  };
}
